/**
 * Form company (04 §2.1, §4.1): the recruits command assigned, and any veterans, as dossier
 * cards. A ranger who has not yet deployed may be renamed - people name them after friends,
 * which is the point. The operation already exists and is saved; BEGIN only opens the map.
 */

import './setup.css';
import type { RangerRecord } from '../../meta/types';
import type { Ctx, Screen } from './context';
import { box, button, el, errorMessage, hinted, show, textField } from './dom';
import { difficultyName, RP_MEANING, serviceName } from './text';
import { dialog } from './widgets/confirm';
import { dossierCard } from './widgets/dossierCard';
import { frame } from './widgets/frame';
import { navHints } from './widgets/keyHints';

const NAME_LENGTH = 14;

function openRename(ctx: Ctx, r: RangerRecord): void {
  const close = (): void => ctx.closeModal();
  const d = dialog(`RENAME ${serviceName(r)}`, close);
  const first = textField('FIRST NAME', r.firstName, NAME_LENGTH);
  const surname = textField('SURNAME', r.surname, NAME_LENGTH);
  const callsign = textField('CALLSIGN', r.callsign, NAME_LENGTH);
  const error = el('div', 'menu-error menu-hidden');
  d.body.append(first.row, surname.row, callsign.row, error);

  const save = (): void => {
    const names = [first.input.value.trim(), surname.input.value.trim(), callsign.input.value.trim()] as const;
    if (names[0] === '' || names[1] === '') {
      error.textContent = 'A RANGER NEEDS A FIRST NAME AND A SURNAME.';
      show(error, true);
      return;
    }
    try {
      ctx.api.renameRanger(r.id, names[0], names[1], names[2]);
    } catch (err) {
      error.textContent = errorMessage(err);
      show(error, true);
      return;
    }
    close();
    // Rebuilt from the profile, so the card shows exactly what was saved.
    ctx.nav.formCompany();
  };
  d.actions.append(button('CANCEL', close), button('SAVE', save));
  ctx.openModal({ ...d.modal, initialFocus: () => first.input });
}

function noOperation(ctx: Ctx): Screen {
  const f = frame('setup', 'FORM COMPANY');
  const toTitle = button('TITLE', () => ctx.nav.title());
  f.body.append(box('menu-col menu-setup-blocked', el('div', 'menu-head-title', 'NO ACTIVE OPERATION'), toTitle));
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => toTitle };
}

export function formCompanyScreen(ctx: Ctx): Screen {
  const op = ctx.api.profile().operation;
  if (op === null || op.status !== 'active') return noOperation(ctx);

  const f = frame('setup', 'FORM COMPANY', navHints('TITLE'), 'THE OPERATION IS SAVED: CONTINUE FROM THE TITLE');
  f.info.append(
    el('span', 'menu-paper', `OPERATION ${op.codename}`),
    hinted('', `RP ${op.rp}`, RP_MEANING),
    el('span', '', difficultyName(op.difficulty)),
  );
  if (op.cadet) f.info.append(el('span', 'menu-chip menu-chip-dim', 'CADET'));

  const grid = el('div', 'menu-company-grid menu-scroll');
  for (const r of op.company) {
    const card = dossierCard(r, { describe: ctx.describe });
    // 04 §4.1: names are the player's until the first deployment; after that the record stands.
    if (r.record.missions === 0) {
      const rename = button('RENAME', () => openRename(ctx, r), 'menu-btn-small');
      rename.setAttribute('aria-label', `RENAME ${serviceName(r)}`);
      card.append(box('menu-dossier-actions', rename));
    }
    grid.append(card);
  }

  const begin = button('BEGIN OPERATION', () => ctx.nav.theatre());
  const rule = 'ONE RANGER GOES IN ALONE. A RANGER WHO DIES ON A MISSION IS GONE FOR GOOD.';
  const foot = box('menu-setup-actions', el('span', 'menu-prose menu-dim menu-grow', rule), begin);
  f.body.append(box('menu-col menu-grow menu-setup', grid, foot));
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => begin };
}
